import React from 'react';
import { Link as ReactRouterDomLink } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Grid, Button, Link, Typography } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew'; 
import { FieldErrors, UseFormMethods } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LogInForm } from './logInForm';
import { LogInFormValues } from '../common/interfaces';
import { routes } from '../common/routes';
import { ErrorTypography } from '../common/errorTypography';

interface LogInViewProps {
    submitLogIn: (data: LogInFormValues) => void;
    fieldsErrors: FieldErrors;
    handleSubmit: UseFormMethods['handleSubmit'];
    register: UseFormMethods['register'];
    returnUrl: string;
    loading: boolean;
    serverError?: string;
    showSso: boolean;
    
}

export const LogInView = ({
    submitLogIn,
    fieldsErrors,
    handleSubmit,
    register,
    returnUrl,
    loading,
    serverError,
    showSso
}: LogInViewProps) => {
    const { t } = useTranslation();

    return (
        <StyledContainer>
            <Grid container spacing={2}>
                <Grid 
                    container 
                    item 
                    direction='row' 
                    alignItems='center' 
                    xs={12}
                >
                    <TitleTypography>
                        {t('forWeatherBugUsers')}
                    </TitleTypography>
                </Grid>
                <LogInForm 
                    submitLogIn={submitLogIn}
                    fieldsErrors={fieldsErrors}
                    handleSubmit={handleSubmit}
                    register={register}
                    loading={loading}
                />
                {serverError
                    && <Grid container item xs={12}>
                        <ErrorTypography serverError={serverError} />
                    </Grid>
                }
                {returnUrl
                    && <AccountQuestionSection 
                        container 
                        item 
                        direction='row' 
                        alignItems='center' 
                        xs={12}
                    >
                        <AccountQuestionTypography>
                            {t('accountQuestion')}
                        </AccountQuestionTypography>
                        <Typography>
                            <SignUpLink
                                href={`/register?ReturnUrl=${returnUrl}`}
                                rel="noopener noreferrer"
                            >
                                {t('signUp')}
                            </SignUpLink>
                        </Typography>
                    </AccountQuestionSection>
                }
                {showSso
                    && <Grid 
                        container 
                        item 
                        direction='row' 
                        alignItems='center' 
                        xs={12}
                    >
                        <DividerGrid container item justify='flex-start' xs={5}>
                            <svg width='100%' height='1'>
                                <rect width='100%' height='1' />
                            </svg>
                        </DividerGrid>
                        <DividerGrid container item xs={2}>
                            <OrTextTypography>{t('or')}</OrTextTypography>
                        </DividerGrid>
                        <DividerGrid container item justify='flex-end' xs={5}>
                            <svg width='100%' height='1'>
                                <rect width='100%' height='1' />
                            </svg>
                        </DividerGrid>
                    </Grid>
                }
                {returnUrl && showSso
                    && <Grid 
                        container 
                        item 
                        direction='row' 
                        alignItems='center' 
                        xs={12}
                    >
                        <Button 
                            component={ReactRouterDomLink}
                            fullWidth 
                            to={`${routes.signInWithSso}?ReturnUrl=${returnUrl}`}
                            variant='outlined'
                            disableElevation
                        >
                            <StyledOpenInNewOcon/>
                            {t('logInWithSso')}
                        </Button>
                    </Grid>
                }
            </Grid>
        </StyledContainer>
    );
};

// TECHDEBT: Clay: We should consider adding a styled components theme to pull these colors and 
// fonts from to make it easier to manage and repeat ourselves less. We should also consider 
// creating some base components to be shared between the different views that share the default 
// styles/overrides. We might be able to tie that stuff into material ui automatically as well.
const StyledContainer = styled(Container)`
    margin-top: 40px;
    font: Roboto;
    font-size: 15px;
    font-weight: 400;
    font-style: normal;
    font-family: sans-serif;
`;

const TitleTypography = styled(Typography)`
    color: #555555;
`;

const SignUpLink = styled(Link)`
    color: #0083C6;
    font-size: 14px;
`;

const AccountQuestionSection = styled(Grid)`
    margin-top: 30px;
    margin-bottom: 30px;
`;

const AccountQuestionTypography = styled(Typography)`
    color: #555555;
    margin-right: 5px;
    font-size: 14px;
`;

const DividerGrid = styled(Grid)`
    fill: #888888;
`;

const OrTextTypography = styled(Typography)`
    color: #888888;
    margin: 0 auto;  
`;

const StyledOpenInNewOcon = styled(OpenInNewIcon)`
    font-size: 16;
    margin-right: 5px;
`;
